import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Upload, FileText, CheckCircle } from 'lucide-react';
import { Category } from './types';

interface Question {
  question_text?: string;
  questionText?: string;
  question?: string;
  question_type?: string;
  questionType?: string;
  type?: string;
}

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File, categoryId?: string, university?: string) => Promise<void>;
  uploading: boolean;
  categories: Category[];
}

export const ImportDialog = ({
  isOpen,
  onClose,
  onImport,
  uploading,
  categories,
}: ImportDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<Question[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedUniversity, setSelectedUniversity] = useState<string>('Vienna');

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();

      if (file.name.endsWith('.json')) {
        const data = JSON.parse(text);
        if (Array.isArray(data)) {
          setFileContent(data);
          setSelectedFile(file);
        } else {
          alert('JSON file must contain an array of questions');
        }
      } else if (file.name.endsWith('.csv')) {
        // For now, just set the file but don't parse CSV
        setSelectedFile(file);
        setFileContent([]);
      }
    } catch (error) {
      alert('Error reading file. Please check the format.');
      console.error('File read error:', error);
    }
  };

  const handleProceed = async () => {
    if (selectedFile) {
      await onImport(selectedFile, selectedCategory === 'none' ? undefined : selectedCategory, selectedUniversity);
      // Reset state after upload is complete
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileContent([]);
    setSelectedCategory('');
    setSelectedUniversity('Vienna');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Import Questions</DialogTitle>
          <DialogDescription>
            Upload a CSV or JSON file containing questions
          </DialogDescription>
        </DialogHeader>

        {!selectedFile ? (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-4">
                Select a CSV or JSON file to import questions
              </p>
              <Input
                type="file"
                accept=".csv,.json"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </div>

            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">Supported formats:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>CSV: question_text, question_type, correct_answer, options, explanation</li>
                <li>JSON: Array of question objects with the same fields</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <FileText className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedFile.size} bytes
                  {fileContent.length > 0 && ` • ${fileContent.length} questions detected`}
                </p>
              </div>
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>

            {fileContent.length > 0 && (
              <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                <p className="font-medium mb-1">Preview:</p>
                <p>First question: {fileContent[0]?.question_text || fileContent[0]?.questionText || fileContent[0]?.question || 'N/A'}</p>
                <p>Question type: {fileContent[0]?.question_type || fileContent[0]?.questionType || fileContent[0]?.type || 'multiple_choice'}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="university">University</Label>
                <Select value={selectedUniversity} onValueChange={setSelectedUniversity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vienna">Medical University of Vienna</SelectItem>
                    <SelectItem value="Graz">Medical University of Graz</SelectItem>
                    <SelectItem value="Innsbruck">Medical University of Innsbruck</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category (Optional)</Label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                  disabled={categories.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign to category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't assign</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedFile(null);
                  setFileContent([]);
                  setSelectedCategory('');
                  setSelectedUniversity('Vienna');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceed}
                disabled={uploading}
                className="flex-1"
              >
                {uploading ? 'Importing...' : 'Proceed with Import'}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};